import DraggableFlatList, {
  RenderItemParams as DraggableRenderItemParams,
  OpacityDecorator,
  RenderItemParams,
  ScaleDecorator,
  ShadowDecorator
} from 'react-native-draggable-flatlist';
import { FlatList, ListRenderItem, TouchableOpacity, View } from 'react-native';
import { ListViewProps, TwSideListProps } from './type';
/*
 * Copyright (c) 2019 3will Corporation and others.
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the 3will License v1.0
 * which accompanies this distribution, and is available at
 *
 *     http://www.3will.cn/licenses/license-v1.0
 *
 * Contributors:
 *    3will - initial API and implementation
 */
import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  TwListRenderInfo,
  TwListRenderItemFunction,
  useGetLatest,
  useKeyExtractor,
  useLabelExtractor,
  useRenderItem
} from '@tw/tw-runtime-react';
import { TwLoadingBlock, TwTypography } from '@tw/tw-components-rn';

import { BoStructure } from '@tw/tw-runtime';
import { DragEndParams } from 'react-native-draggable-flatlist/src/types';
import { isNil } from 'lodash-es';
import { styles } from './styles';

export function TwSideList<Value extends BoStructure = BoStructure>(props: TwSideListProps<Value>) {
  const { loading, style } = props;
  const containerStyle = useMemo(() => [styles.container, style], [style]);
  if (loading) {
    return (
      <View style={containerStyle} testID={'TwSideList_container'}>
        <TwLoadingBlock />
      </View>
    );
  }
  return <InnerSideList {...props} />;
}

function InnerSideList<Value extends BoStructure = BoStructure>(props: TwSideListProps<Value>) {
  const {
    sortable,
    activeKey,
    items,
    renderItem: renderItemProp,
    keyExtractor,
    labelExtractor,
    style,
    itemWrapStyle,
    disableDropTo,
    onActiveItemChange,
    onSortEnd,
    onSortBegin
  } = props;

  const selectedWrapStyle = useMemo(() => [styles.itemWrap, itemWrapStyle, styles.itemWrap__active], [itemWrapStyle]);

  const getKey = useKeyExtractor(keyExtractor);
  const getLabel = useLabelExtractor(labelExtractor);
  const containerStyle = useMemo(() => [styles.container, style], [style]);
  const selectedLabelStyle = useMemo(() => [styles.itemLabel, styles.itemLabel_active], []);
  const getDisableDropTo = useGetLatest(disableDropTo);
  const checkDisableDropTo = useCallback(
    (index: number) => {
      const disableDrop = getDisableDropTo();
      return disableDrop ? disableDrop(index) : false;
    },
    [getDisableDropTo]
  );
  const renderItem = useRenderItem(renderItemProp, (info) => (
    <TwTypography.Text size={'sm'} style={info.isSelected ? selectedLabelStyle : styles.itemLabel}>
      {getLabel(info.item, info.index)}
    </TwTypography.Text>
  ));

  const renderDraggableItem = useCallback(
    (info: DraggableRenderItemParams<any>) => {
      //
      const { item, isActive, drag, getIndex } = info;
      const index = getIndex();
      const key = getKey(item, index!);
      const isSelected = key === activeKey;
      const disabled = isNil(index) ? false : checkDisableDropTo(index);
      return (
        <TouchableOpacity
          disabled={isActive}
          onLongPress={disabled ? undefined : drag}
          onPress={() => onActiveItemChange?.(item)}
        >
          <View style={isSelected ? selectedWrapStyle : styles.itemWrap}>
            {renderItem({
              item: item,
              index: index!,
              isSelected: isSelected
            })}
          </View>
        </TouchableOpacity>
      );
    },
    [activeKey, checkDisableDropTo, getKey, onActiveItemChange, renderItem, selectedWrapStyle]
  );

  const renderItemWrap: TwListRenderItemFunction<any> = useCallback(
    ({ item, index }: TwListRenderInfo<any>) => {
      const key = getKey(item, index);
      const isSelected = key === activeKey;
      return (
        <TouchableOpacity onPress={() => onActiveItemChange?.(item)} key={getKey(item, index)}>
          <View style={isSelected ? selectedWrapStyle : styles.itemWrap}>
            {renderItem({
              item: item,
              index: index,
              isSelected: isSelected
            })}
          </View>
        </TouchableOpacity>
      );
    },
    [activeKey, getKey, onActiveItemChange, renderItem, selectedWrapStyle]
  );
  const renderList = () => {
    if (sortable) {
      return (
        <InnerDraggableList
          items={items}
          keyExtractor={getKey}
          renderItem={renderDraggableItem}
          disableDropTo={checkDisableDropTo}
          onSortEnd={onSortEnd as any}
          onSortBegin={onSortBegin}
        />
      );
    }
    return <InnerFlatList items={items} keyExtractor={getKey} renderItem={renderItemWrap} />;
  };
  return (
    <View style={containerStyle} testID={'TwSideList_container'}>
      {renderList()}
    </View>
  );
}

const InnerFlatList: FC<ListViewProps> = (props) => {
  const { items, keyExtractor, renderItem: renderItemProp } = props;
  const renderItem: ListRenderItem<any> = useCallback(
    (info) => {
      return (
        <>
          {(renderItemProp as any)!({
            item: info.item,
            index: info.index
          })}
        </>
      );
    },
    [renderItemProp]
  );
  return (
    <FlatList
      showsVerticalScrollIndicator={false}
      data={items}
      keyExtractor={keyExtractor as any}
      renderItem={renderItem}
    />
  );
};

const InnerDraggableList: FC<ListViewProps> = (props) => {
  const { items, keyExtractor, renderItem: renderItemProp, onSortBegin, onSortEnd, disableDropTo } = props;
  const [disableDrop, setDisableDrop] = useState(false);

  const renderItem = useCallback(
    (params: RenderItemParams<any>) => {
      return (
        <ShadowDecorator>
          <ScaleDecorator>
            <OpacityDecorator>{(renderItemProp as any)!(params)}</OpacityDecorator>
          </ScaleDecorator>
        </ShadowDecorator>
      );
    },
    [renderItemProp]
  );

  const getOnSortEnd = useGetLatest(onSortEnd);
  const getOnSortBegin = useGetLatest(onSortBegin);
  const handleDragEnd = useCallback(
    (params: DragEndParams<any>) => {
      const disable = disableDropTo!(params.to);
      if (disable) {
        return;
      }
      getOnSortEnd()?.(params);
    },
    [disableDropTo, getOnSortEnd]
  );
  const handleSortBegin = useCallback(
    (index: number) => {
      const disable = disableDropTo!(index);
      setDisableDrop(disable);
      getOnSortBegin()?.(index);
    },
    [disableDropTo, getOnSortBegin]
  );
  const handlePlaceholderIndexChange = useCallback(
    (index: number) => {
      const disable = disableDropTo!(index);
      setDisableDrop(disable);
    },
    [disableDropTo]
  );
  return (
    <DraggableFlatList
      showsVerticalScrollIndicator={false}
      data={items}
      keyExtractor={keyExtractor as any}
      renderItem={renderItem}
      onDragBegin={handleSortBegin}
      onDragEnd={handleDragEnd}
      onPlaceholderIndexChange={handlePlaceholderIndexChange}
      renderPlaceholder={() => {
        return <View style={disableDrop ? styles.dragItemPlaceholder__disabled : styles.dragItemPlaceholder} />;
      }}
    />
  );
};
